// eslint-disable-next-line no-restricted-imports
import {reportError} from '@github-ui/failbot'
import {IssuesLoadingSkeleton} from '@github-ui/issues-loading-skeleton'
import {DiscussionCategoryPicker} from '@github-ui/item-picker/DiscussionCategoryPicker'
import type {IssueViewerQueryIdentifier} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {ISSUE_VIEWER_CACHE_TTL_MS} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {useQuery} from '@github-ui/react-query'
import {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {ConvertToDiscussionDialogServerQuery} from '@github-ui/server-persisted-queries'
import type {ConvertToDiscussionDialogServerQueryQuery} from '@github-ui/server-persisted-queries/types/ConvertToDiscussionDialogServerQuery'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {testIdProps} from '@github-ui/test-id-props'
import {useAnalytics} from '@github-ui/use-analytics'
import {CheckIcon, XIcon} from '@primer/octicons-react'
import {Banner, FormControl} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Suspense, useCallback, useId, useMemo, useState} from 'react'

import {BUTTON_LABELS} from '../constants/buttons'
import {LABELS} from '../constants/labels'
import {
  type ConvertIssueToDiscussionMutationResponse,
  useConvertIssueToDiscussionMutation,
} from '../mutations/convert-issue-to-discussion-mutation'
import {IssueLifecyclePayloadError} from '../mutations/issue-lifecycle-mutation'
import type {
  ConvertToDiscussionDialogIssueNode,
  ConvertToDiscussionRepository,
} from './convert-to-discussion-dialog-query-types'
import styles from './ConvertToDiscussionDialog.module.css'
import {convertToDiscussionDialogQueryKey, fetchConvertToDiscussionDialog} from './fetch-convert-to-discussion-dialog'

export type ConvertToDiscussionDialogProps = {
  owner: string
  repository: string
  number: number
  primaryIdentifier?: IssueViewerQueryIdentifier
  onClose: () => void
}

export const ConvertToDiscussionDialog = ({owner, repository, number, ...props}: ConvertToDiscussionDialogProps) => {
  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createServerPersistedQueryPersister({
        queryHash: ConvertToDiscussionDialogServerQuery,
        sendAnalyticsEvent,
        maxAgeMs: ISSUE_VIEWER_CACHE_TTL_MS,
        // Restored data may be stale within its TTL, so revalidate after restoring it.
        alwaysRefetch: true,
      }),
    [sendAnalyticsEvent],
  )

  const query = useQuery({
    queryKey: convertToDiscussionDialogQueryKey(owner, repository, number),
    queryFn: ({signal}) => fetchConvertToDiscussionDialog(owner, repository, number, signal),
    gcTime: ISSUE_VIEWER_CACHE_TTL_MS,
    persister,
  })

  if (query.isPending) return <DialogLoadingSkeleton />
  // A failed background revalidation keeps the restored data, so only surface the error when
  // there is nothing usable to show.
  if (query.isError && !query.data) throw query.error

  return (
    <ConvertToDiscussionDialogInternal
      {...props}
      owner={owner}
      repository={repository}
      number={number}
      data={query.data}
    />
  )
}

type ConvertToDiscussionDialogPropsInternal = ConvertToDiscussionDialogProps & {
  data: ConvertToDiscussionDialogServerQueryQuery
}
const ConvertToDiscussionDialogInternal = ({
  data,
  owner,
  repository: repositoryName,
  number,
  primaryIdentifier,
  onClose,
}: ConvertToDiscussionDialogPropsInternal) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>()
  const [isConverting, setIsConverting] = useState(false)
  const [showError, setShowError] = useState(false)
  const onError = useCallback((error: Error) => {
    const messages = error instanceof IssueLifecyclePayloadError ? error.messages : [error.message]
    for (const message of messages) {
      reportError(formatError(message))
    }
    setShowError(true)
    setIsConverting(false)
  }, [])
  const onCompleted = useCallback((response: ConvertIssueToDiscussionMutationResponse) => {
    if (response.convertIssueToDiscussion?.discussion) {
      if (ssrSafeWindow) {
        ssrSafeWindow.location.href = response.convertIssueToDiscussion.discussion.url
      }
      return
    }
    for (const error of response.convertIssueToDiscussion?.errors || []) {
      reportError(formatError(error.message))
    }
    setShowError(true)
    setIsConverting(false)
  }, [])
  const convertIssueMutation = useConvertIssueToDiscussionMutation({
    owner,
    repository: repositoryName,
    number,
    primaryIdentifier,
    onError,
    onCompleted,
  })

  const issueProperties = data?.node
  const repository = issueProperties && 'repository' in issueProperties ? issueProperties.repository : undefined

  const handleConvertToDiscussion = useCallback(() => {
    if (!selectedCategoryId || isConverting) return
    setIsConverting(true)

    convertIssueMutation.mutate({
      input: {categoryId: selectedCategoryId},
    })
  }, [convertIssueMutation, isConverting, selectedCategoryId])

  const categoryId = useId()

  return (
    <Dialog
      title={LABELS.convertToDiscussion.title}
      onClose={onClose}
      footerButtons={[
        {content: BUTTON_LABELS.cancel, onClick: onClose, disabled: isConverting},
        {
          content: BUTTON_LABELS.acknowledgeAndConvertToDiscussion,
          buttonType: 'danger',
          onClick: handleConvertToDiscussion,
          disabled: !selectedCategoryId || isConverting,
          loading: isConverting,
          ...testIdProps('convertButton'),
        },
      ]}
      width="large"
      renderBody={({children}) => {
        return (
          <div className="tmp-p-3">
            {showError && (
              <Banner
                title="Convert issue failed"
                hideTitle
                description={LABELS.somethingWentWrong}
                variant="critical"
                className="mb-2"
              />
            )}
            {children}
          </div>
        )
      }}
    >
      <Suspense fallback={<DialogLoadingSkeleton />}>
        {issueProperties && 'repository' in issueProperties && <WhatHappens issueProperties={issueProperties} />}
        <FormControl>
          <FormControl.Label htmlFor={categoryId}>{LABELS.convertToDiscussion.selectCategoryTitle}</FormControl.Label>
          {repository && (
            <CategoryPicker categoryId={categoryId} onSelect={setSelectedCategoryId} repository={repository} />
          )}
        </FormControl>
      </Suspense>
    </Dialog>
  )
}

export type ConvertToDiscussionDialogCategoryPickerFragment = Pick<
  ConvertToDiscussionRepository,
  'discussionCategories'
>

type CategoryPickerProps = {
  repository: ConvertToDiscussionDialogCategoryPickerFragment
  categoryId?: string
  onSelect: (categoryId: string) => void
}

const CategoryPicker = ({repository: data, categoryId, onSelect}: CategoryPickerProps) => {
  const discussionCategories = useMemo(() => {
    return (
      data?.discussionCategories?.edges?.map(edge => ({
        id: edge?.node?.id || '',
        name: edge?.node?.name || '',
      })) || []
    )
  }, [data])

  return (
    <DiscussionCategoryPicker categoryId={categoryId} discussionCategories={discussionCategories} onSelect={onSelect} />
  )
}

function formatError(message: string) {
  return new Error(LABELS.convertToDiscussion.error(message))
}

const DialogLoadingSkeleton = () => (
  <>
    <div className={styles.firstLoader}>
      <IssuesLoadingSkeleton height="sm" width="80%" />
    </div>
    <div className={styles.container}>
      <div className={styles.commonLoader}>
        <IssuesLoadingSkeleton height="sm" width="50%" />
      </div>
      <div className={styles.commonLoader}>
        <IssuesLoadingSkeleton height="sm" width="90%" />
      </div>
      <div className={styles.commonLoader}>
        <IssuesLoadingSkeleton height="sm" width="87%" />
      </div>
    </div>
    <div className={styles.commonLoader}>
      <IssuesLoadingSkeleton height="dm" width="50%" />
    </div>
    <div className={styles.commonLoader}>
      <IssuesLoadingSkeleton height="dm" width="100%" />
    </div>
  </>
)

export type ConvertToDiscussionDialogIssuePropertiesFragment = Pick<
  ConvertToDiscussionDialogIssueNode,
  'comments' | 'reactions' | 'assignees' | 'projectsV2' | 'milestone'
>

type WhatHappensProps = {
  issueProperties: ConvertToDiscussionDialogIssuePropertiesFragment
}

const WhatHappens = ({issueProperties: issue}: WhatHappensProps) => {
  const {comments, reactions, assignees, projectsV2, milestone} = issue

  const affirmations = useMemo(() => {
    const results = [
      LABELS.convertToDiscussion.affirmations.closedAndLocked,
      LABELS.convertToDiscussion.affirmations.same,
    ]

    if ((comments && comments.totalCount > 0) || (reactions && reactions.totalCount > 0)) {
      results.push(LABELS.convertToDiscussion.affirmations.commentsAndReactions)
    }

    return results
  }, [comments, reactions])

  const warnings = useMemo(() => {
    const results = []

    if (assignees && assignees.totalCount > 0) {
      results.push(LABELS.convertToDiscussion.warnings.assignees)
    }

    if (projectsV2 && projectsV2.totalCount > 0) {
      results.push(LABELS.convertToDiscussion.warnings.projects)
    }

    if (milestone) {
      results.push(LABELS.convertToDiscussion.warnings.milestone)
    }

    return results
  }, [assignees, projectsV2, milestone])

  return (
    <>
      <p className="tmp-mb-3">{LABELS.convertToDiscussion.whatHappens}</p>

      <ul className="list-style-none tmp-ml-3 tmp-mb-3">
        {affirmations.map((text, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <Result key={index} success text={text} />
        ))}
        {warnings.map((text, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <Result key={index} success={false} text={text} />
        ))}
      </ul>
    </>
  )
}

type ResultProps = {
  success: boolean
  text: string
}
const Result = ({success, text}: ResultProps) => {
  return (
    <li className="mb-1">
      {success ? <CheckIcon className="fgColor-success mr-1" /> : <XIcon className="fgColor-danger mr-1" />}
      {/* eslint-disable-next-line react-google-translate/no-conditional-text-nodes-with-siblings */}
      {text}
    </li>
  )
}
